import { Injectable } from '@angular/core';
import { NotificationsService } from 'api/notifications.api';
import { GetTicketsResponse, TicketingApiService, PatchTicketRequest } from '@kempoweroyj/ngx-ticketing-api-client';
import { Observable, Subject } from 'rxjs';
import { auditTime, exhaustMap, filter, publishReplay, refCount, startWith } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TicketsService {
    private _ticketsObservableCache: { [stationId: string]: Observable<GetTicketsResponse> } = {};
    private _ticketsRefreshRequest$: { [stationId: string]: Subject<void> } = {};

    constructor(
        private ticketingApi: TicketingApiService,
        private notifications: NotificationsService
    ) {}

    ticketsRefreshRequest$(stationId: string) {
        if (!this._ticketsRefreshRequest$[stationId]) {
            this._ticketsRefreshRequest$[stationId] = new Subject<void>();
            this.notifications.notification$
                .pipe(
                    filter(
                        (n) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.changedSchemas?.includes('ticket') &&
                            n.stationId === stationId
                    )
                )
                .subscribe(() => {
                    this._ticketsRefreshRequest$[stationId].next();
                });
        }
        return this._ticketsRefreshRequest$[stationId];
    }

    tickets$(stationId: string): Observable<GetTicketsResponse> {
        if (!this._ticketsObservableCache[stationId]) {
            this._ticketsObservableCache[stationId] = this.ticketsRefreshRequest$(stationId).pipe(
                auditTime(0),
                startWith([]),
                exhaustMap(() =>
                    this.ticketingApi.getTickets(stationId).catch((e) => {
                        console.error('tickets$', e);
                        return Promise.resolve<GetTicketsResponse>(undefined);
                    })
                ),
                filter((res) => !!res),
                publishReplay(1),
                refCount()
            );
        }
        return this._ticketsObservableCache[stationId];
    }

    async toggleTicketPin(stationId: string, ticketId: string, isPinned: boolean): Promise<void> {
        const patchRequest: PatchTicketRequest = {
            ticket: {
                pinned: isPinned,
            },
        };

        try {
            await this.ticketingApi.patchTicket(stationId, ticketId, patchRequest);
            // Refresh the tickets cache for this station only if patch succeeded
            if (this._ticketsRefreshRequest$[stationId]) {
                this._ticketsRefreshRequest$[stationId].next();
            }
        } catch (error) {
            console.error('Failed to patch ticket:', error);
            throw error;
        }
    }
}
